import { useCallback } from 'react'
import { css } from '@emotion/react'
import { Link } from 'gatsby'
import { string, array, node } from 'prop-types'
import { Checkbox, DesignToken } from '@livechat/design-system-react-components'

import { canUseWindow } from '@/utils/browser'
import { noop } from '@/utils/func'
import { useEnhancedList } from '@/context/EnhancedList'
import { URLS, FILTERS, EXPERTS_LIST_STORAGE_KEY } from '@/constants'
import Icon from '../Icon'
import Collapse from '../Collapse'

const collapseMenuCss = css`
  .collapse__label {
    align-items: flex-start;
    justify-content: space-between;

    svg {
      max-width: 20px;
      max-height: 20px;
      margin-right: 10px;
    }

    > a {
      display: flex;
      width: 100%;
      font-size: 16px;
      line-height: 16px;
      text-transform: none;
      margin-left: 0;
      margin-bottom: 10px;
    }
  }

  .collapse__content {
    a {
      margin: 0;
    }
  }
`

const serviceFiltersCss = css`
  margin-top: 5px;
  margin-left: 38px;

  .ds-checkbox {
    width: 100%;

    label {
      display: flex;
      text-transform: none;
      font-weight: normal;
      font-size: 16px;
      line-height: 28px;
      margin-bottom: 14px;

      &:hover div {
        color: var(${DesignToken.ActionPrimaryHover});
      }
    }
  }
`

const Services = ({
  label = 'Partners',
  labelIcon,
  options,
  links,
  ...rest
}) => {
  const { filters, filtersOptions, updateFilters, clearFilter } =
    useEnhancedList()

  const servicesFilter = filters && filters[FILTERS.SERVICE.ID]
  const servicesOptions =
    options || (filtersOptions && filtersOptions[FILTERS.SERVICE.ID]) || []

  const handleFilterClick = useCallback(
    (service) => {
      if (updateFilters) {
        updateFilters(FILTERS.SERVICE.ID, service.VALUE, true)
      }
    },
    [updateFilters]
  )

  return (
    <Collapse
      isOpenOnInit
      sectionKey={FILTERS.SERVICE.ID}
      sectionLink={URLS.EXPERTS.LINK}
      storageKey={EXPERTS_LIST_STORAGE_KEY}
      css={collapseMenuCss}
      label={({ inputRef }) => {
        const handleOnClick = () => {
          if (!inputRef.current.checked) {
            inputRef.current.click()
          }

          if (clearFilter) {
            clearFilter(FILTERS.SERVICE.ID)
          }
        }

        return (
          <Link
            to="/partners/"
            onClick={handleOnClick}
            activeClassName="active-link"
          >
            <Icon label={labelIcon || URLS.APPS.LABEL} />
            <span>{label}</span>
          </Link>
        )
      }}
      content={
        <div css={serviceFiltersCss} data-test="category-filters">
          {links}

          {servicesOptions.map((service) => {
            const handleOnClick = (e) => {
              e.preventDefault()
              handleFilterClick(service)
            }

            const isChecked = servicesFilter
              ? servicesFilter.includes(service.VALUE)
              : false

            const isExpertsPageWithFilters =
              canUseWindow &&
              String(window.location.pathname).startsWith('/partners/?')

            const linkTo = `/partners/${
              !isExpertsPageWithFilters || !servicesFilter
                ? `?services=${service.VALUE}`
                : ''
            }`

            return (
              <div key={`service-${service.LABEL}`} onClick={handleOnClick}>
                <Link to={linkTo}>
                  <Checkbox
                    className="ds-checkbox"
                    id={service.LABEL}
                    checked={isChecked}
                    onChange={noop}
                    data-test="category-checkbox"
                  >
                    {service.LABEL}
                  </Checkbox>
                </Link>
              </div>
            )
          })}
        </div>
      }
      {...rest}
    />
  )
}

Services.propTypes = {
  label: string,
  labelIcon: string,
  options: array,
  links: node,
}

export default Services
